import React from "react";
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Stack, Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAnomalies } from "../../../api/analytics";

interface Props {
  fleetId: number;
  from: string;
  to: string;
}

const SEVERITY_FOR_SCORE = (score: number): "error" | "warning" | "info" =>
  score >= 6 ? "error" : score >= 4 ? "warning" : "info";

/**
 * Anomalies.
 *
 * Deliberately statistical and peer-relative rather than a model score: a fleet
 * manager can argue with "2.4x the median of its 18 comparable vehicles" and
 * act on it. An opaque score gets ignored after the second false positive.
 *
 * The method is printed at the bottom on purpose — hiding it would make the
 * findings look like magic, and magic does not survive a challenge from an
 * operations team.
 */
export const Anomalies: React.FC<Props> = ({ fleetId, from, to }) => {
  const { t } = useTranslation();
  const { anomalies, method, isLoading } = useAnomalies(fleetId, from, to);

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  }

  if (anomalies.length === 0) {
    return (
      <Alert severity="success">
        {t(
          "analytics.noAnomalies",
          "Aucune anomalie detectee sur la periode. Les segments comptant moins de 5 vehicules comparables sont exclus : l'ecart y serait du bruit, pas un signal."
        )}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {anomalies.map((anomaly, index) => (
        <Card key={`${anomaly.type}-${anomaly.vehicle_id}-${index}`}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip size="small" color={SEVERITY_FOR_SCORE(anomaly.score)} label={anomaly.type} />
                  <Chip size="small" variant="outlined" label={`#${anomaly.vehicle_id}`} />
                </Stack>

                <Typography variant="body2">{anomaly.explanation}</Typography>

                {anomaly.peer_group && (
                  <Typography variant="caption" color="text.secondary">
                    {t("analytics.peerGroup", "Segment comparable")} : {anomaly.peer_group.energy} /{" "}
                    {anomaly.peer_group.category} ({anomaly.peer_group.size}{" "}
                    {t("analytics.vehicles", "vehicules")})
                  </Typography>
                )}
              </Box>

              <Stack alignItems="flex-end">
                <Typography variant="caption" color="text.secondary">
                  {t("analytics.score", "Score")}
                </Typography>
                <Typography variant="h6" fontWeight={700}>{anomaly.score}</Typography>
                {anomaly.deviation_pct !== null && anomaly.deviation_pct !== undefined && (
                  <Typography variant="caption" color="error.main">
                    {anomaly.deviation_pct > 0 ? "+" : ""}{anomaly.deviation_pct}%
                  </Typography>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}

      <Typography variant="caption" color="text.secondary">
        {t("analytics.method", "Methode")} : {method}
      </Typography>
    </Stack>
  );
};
